import { create } from 'zustand';
import apiClient from '@/services/letsparkyClient';
import { 
  Device as ApiDevice, 
  Parking as ApiParking,
  User as ApiUser,
  PaginatedResponse, 
  ListResponse,
  handleApiError 
} from '@/types/api';
import { Parking, ParkingStatus } from '@letsparky/api-v2-client';

export interface DashboardStats {
  users: number;
  tenants: number;
  devices: number;
  parkings: number;
  payments: number;
}

export interface ParkingSession extends Omit<ApiParking, 'status'> {
  status: string;
}

export interface Device extends Omit<ApiDevice, 'status'> {
}

interface DashboardState {
  stats: DashboardStats | null;
  isLoadingStats: boolean;
  statsError: string | null;
  
  parkingSessions: ParkingSession[];
  isLoadingParkingSessions: boolean;
  parkingSessionsError: string | null;
  
  devices: Device[];
  isLoadingDevices: boolean;
  devicesError: string | null;

  payments: any[];
  isLoadingPayments: boolean;
  paymentsError: string | null;
  
  fetchStats: (isSystem: boolean, tenantId?: string) => Promise<void>;
  fetchParkingSessions: (status: ParkingStatus | ParkingStatus[]) => Promise<void>;
  fetchDevices: () => Promise<void>;
  fetchPayments: () => Promise<void>;
  fetchAllDashboardData: (isSystem: boolean, tenantId?: string) => Promise<void>;
}

export const useDashboardStore = create<DashboardState>()((set, get) => ({
  stats: null,
  isLoadingStats: false,
  statsError: null,
  
  parkingSessions: [],
  isLoadingParkingSessions: false,
  parkingSessionsError: null,
  
  devices: [],
  isLoadingDevices: false,
  devicesError: null,

  payments: [],
  isLoadingPayments: false,
  paymentsError: null,
  
  fetchStats: async (isSystem: boolean, tenantId?: string) => {
    set({ isLoadingStats: true, statsError: null });
    try {
      if (isSystem) {
        const [usersRes, tenantsRes, devicesRes, parkingsRes, paymentsRes] = await Promise.all([
          apiClient.users.listPaginated({ page: 1, limit: 1 }), 
          apiClient.tenants.listPaginated({ page: 1, limit: 1 }),
          apiClient.devices.listPaginated({ page: 1, limit: 1 }),
          apiClient.parkings.listPaginated({ page: 1, limit: 1 }),
          apiClient.payments.listPaginated({ page: 1, limit: 1 })
        ]);

        set({
          stats: {
            users: usersRes.pagination.total || 0,
            tenants: tenantsRes.pagination.total || 0,
            devices: devicesRes.pagination.total || 0,
            parkings: parkingsRes.pagination.total || 0,
            payments: paymentsRes.pagination.total || 0
          },
          isLoadingStats: false,
        });
      } else {
        if (!tenantId) {
          throw new Error('Tenant ID is required for non-system users');
        }

        const [usersRes, devicesRes, parkingsRes, paymentsRes] = await Promise.all([
          apiClient.users.list({ tenant_id: tenantId }) as unknown as ListResponse<ApiUser>,
          apiClient.devices.listPaginated({ page: 1, limit: 1 }, { tenant_id: tenantId }),
          apiClient.parkings.listPaginated({ page: 1, limit: 1}, {status: [ParkingStatus.COMPLETED], tenant_id: tenantId }),
          apiClient.payments.listPaginated({ page: 1, limit: 1 }, { tenant_id: tenantId }),
        ]);

        set({
          stats: {
            users: usersRes.total || 0,
            tenants: 0,
            devices: devicesRes.pagination.total || 0,
            parkings: parkingsRes.pagination.total || 0,
            payments: paymentsRes.pagination.total || 0
          },
          isLoadingStats: false,
        });
      }
    } catch (error) {
      set({ 
        statsError: handleApiError(error),
        isLoadingStats: false
      });
    }
  },
  
  fetchParkingSessions: async (status: ParkingStatus | ParkingStatus[]) => {
    set({ isLoadingParkingSessions: true, parkingSessionsError: null });
    try {
      const response = await apiClient.parkings.list({limit: 10000, status: status as ParkingStatus });
      
      if (!response) {
        throw new Error('No data returned from API');
      }
      
      set({
        parkingSessions: response || [],
        isLoadingParkingSessions: false
      });
    } catch (error) {
      set({
        parkingSessionsError: handleApiError(error),
        isLoadingParkingSessions: false
      });
    }
  },
  
  fetchDevices: async () => {
    set({ isLoadingDevices: true, devicesError: null });
    try {
      const response = await apiClient.devices.list();
      
      if (!response) {
        throw new Error('No data returned from API');
      }
      
      set({
        devices: response || [],
        isLoadingDevices: false
      });
    } catch (error) {
      set({
        devicesError: handleApiError(error),
        isLoadingDevices: false
      });
    }
  },

  fetchPayments: async () => {
    set({ isLoadingPayments: true, paymentsError: null });
    try {
      const response = await apiClient.payments.listPaginated({ page: 1, limit: 1000 });

      set({
        payments: response.data || [],
        isLoadingPayments: false
      });
    } catch (error) {
      set({
        paymentsError: handleApiError(error),
        isLoadingPayments: false
      });
    }
  },
  
  fetchAllDashboardData: async (isSystem: boolean, tenantId?: string) => {
    try {
      await Promise.all([
        get().fetchStats(isSystem, tenantId),
        get().fetchDevices(),
        get().fetchParkingSessions([ParkingStatus.COMPLETED, ParkingStatus.ACTIVE, ParkingStatus.CANCELLED]),
        get().fetchPayments()
      ]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  },
}));
